import { useId, useMemo, useState } from "react";
import { ROLE_LABELS, USER_STATUS_LABELS, api, useApiQuery } from "../../api";
import type { ConnectionEvent, ConnectionSummary } from "../../api";
import { Badge, Button, Card, EmptyState, Field, Modal, RoleBadge, Table, cx } from "../../components";
import type { TableColumn } from "../../components";
import { formatDateTime, formatRelatif, isoDateTime } from "../../format";
import { AsyncBoundary, PageHeader } from "../../shell";
import styles from "./Esn.module.css";

/** Les quatre espaces d'un compte, tels que le filtre les propose. */
type Espace = "tous" | "esn" | "consultants" | "clients";

const ESPACES: { cle: Espace; libelle: string }[] = [
  { cle: "tous", libelle: "Tous" },
  { cle: "esn", libelle: "Équipe ESN" },
  { cle: "consultants", libelle: "Consultants" },
  { cle: "clients", libelle: "Clients" },
];

function espaceDe(c: ConnectionSummary): Espace {
  if (c.user.role === "consultant") {
    return "consultants";
  }
  return c.user.company_id === null ? "esn" : "clients";
}

/** Ce que dit chaque ligne d'historique, en clair. */
const ACTIONS: Record<ConnectionEvent["action"], { libelle: string; ton: "success" | "danger" | "neutral" | "info" }> = {
  login_succeeded: { libelle: "Connexion", ton: "success" },
  login_failed: { libelle: "Échec de connexion", ton: "danger" },
  logout: { libelle: "Déconnexion", ton: "neutral" },
  invitation_accepted: { libelle: "Invitation acceptée", ton: "info" },
  password_reset: { libelle: "Mot de passe réinitialisé", ton: "info" },
};

const MOTIFS: Record<string, string> = {
  bad_password: "mot de passe erroné",
  inactive: "compte inactif",
  locked: "compte verrouillé",
};

/** Une date avec son ancienneté : « il y a 3 jours », la date exacte en infobulle. */
function Quand({ iso, vide }: { iso: string | null; vide: string }) {
  if (iso === null) {
    return <span className={styles.jamais}>{vide}</span>;
  }
  return (
    <time dateTime={isoDateTime(iso)} title={formatDateTime(iso)}>
      {formatRelatif(iso)}
    </time>
  );
}

/**
 * Les connexions de tous les comptes, pour l'administrateur ESN : qui s'est
 * connecté quand, et l'historique de chacun. De quoi suivre l'adoption chez un
 * client, repérer un compte dormant ou un compte qu'on essaie de forcer.
 *
 * Les filtres s'appliquent ici, sur la liste complète : elle tient en une
 * réponse à l'échelle d'une ESN, et filtrer sans aller-retour rend la saisie
 * instantanée.
 */
export function EsnConnexionsPage() {
  const liste = useApiQuery((o) => api.listConnections(o), []);
  const [espace, setEspace] = useState<Espace>("tous");
  const [entreprise, setEntreprise] = useState("");
  const [recherche, setRecherche] = useState("");
  const [choisi, setChoisi] = useState<ConnectionSummary | null>(null);
  const nomFiltre = useId();

  const connexions = useMemo(() => liste.data?.connections ?? [], [liste.data]);
  const entreprises = useMemo(
    () => [...new Set(connexions.map((c) => c.company_name).filter((n): n is string => n !== null))].sort((a, b) => a.localeCompare(b, "fr")),
    [connexions],
  );
  const visibles = useMemo(() => {
    const texte = recherche.trim().toLowerCase();
    return connexions.filter(
      (c) =>
        (espace === "tous" || espaceDe(c) === espace) &&
        (entreprise === "" || c.company_name === entreprise) &&
        (texte === "" || c.user.email.includes(texte)),
    );
  }, [connexions, espace, entreprise, recherche]);

  const colonnes: TableColumn<ConnectionSummary>[] = [
    {
      key: "personne",
      header: "Compte",
      render: (c) => (
        <span className={styles.compte}>
          <span>{c.user.email}</span>
          <span className={styles.compteMeta}>
            <RoleBadge role={c.user.role} size="sm" />
            {c.user.status !== "active" && (
              <Badge tone={c.user.status === "disabled" ? "danger" : "warning"} size="sm">
                {USER_STATUS_LABELS[c.user.status]}
              </Badge>
            )}
          </span>
        </span>
      ),
    },
    {
      key: "entreprise",
      header: "Entreprise",
      render: (c) => c.company_name ?? (c.user.role === "consultant" ? "Consultant de l'ESN" : "Équipe ESN"),
    },
    {
      key: "connexion",
      header: "Dernière connexion",
      render: (c) => <Quand iso={c.last_login_at} vide="Jamais connecté" />,
    },
    {
      key: "activite",
      header: "Dernière activité",
      render: (c) => <Quand iso={c.last_seen_at} vide="—" />,
    },
    {
      key: "echecs",
      header: "Échecs",
      align: "right",
      render: (c) =>
        c.failed_logins > 0 ? (
          <Badge tone="danger" size="sm">
            {c.failed_logins} d'affilée
          </Badge>
        ) : (
          <span className={styles.jamais}>0</span>
        ),
    },
  ];

  return (
    <>
      <PageHeader
        eyebrow="Espace ESN"
        title="Connexions"
        description="Quand chaque compte s'est connecté, et son historique. Cliquez sur une ligne pour le détail."
        actions={
          <Button variant="ghost" onClick={liste.reload} loading={liste.loading}>
            Actualiser
          </Button>
        }
      />

      <Card>
        <div className={styles.barreFiltres}>
          <fieldset className={styles.filtres}>
            <legend className={styles.legende}>Espace</legend>
            <div className={styles.choix}>
              {ESPACES.map((o) => (
                <label key={o.cle} className={cx(styles.puce, espace === o.cle && styles.puceActive)}>
                  <input
                    type="radio"
                    name={nomFiltre}
                    value={o.cle}
                    checked={espace === o.cle}
                    onChange={() => setEspace(o.cle)}
                    className="lde-visually-hidden"
                  />
                  <span>{o.libelle}</span>
                </label>
              ))}
            </div>
          </fieldset>
          <Field label="Entreprise">
            {(commande) => (
              <select {...commande} value={entreprise} onChange={(e) => setEntreprise(e.target.value)}>
                <option value="">Toutes</option>
                {entreprises.map((n) => (
                  <option key={n} value={n}>
                    {n}
                  </option>
                ))}
              </select>
            )}
          </Field>
          <Field label="Rechercher une adresse">
            {(commande) => (
              <input {...commande} type="search" placeholder="ex. alice@" value={recherche} onChange={(e) => setRecherche(e.target.value)} />
            )}
          </Field>
        </div>
      </Card>

      <AsyncBoundary
        state={liste}
        errorTitle="Connexions illisibles"
        skeleton={<Table columns={colonnes} rows={[]} rowKey={(c) => c.user.id} caption="Connexions" loading animateRows={false} />}
      >
        {() => (
          <Card padding="none">
            <Table
              columns={colonnes}
              rows={visibles}
              rowKey={(c) => c.user.id}
              caption={`${visibles.length} compte${visibles.length > 1 ? "s" : ""}, les connexions les plus récentes d'abord`}
              onRowClick={(c) => setChoisi(c)}
              empty={<EmptyState compact title="Aucun compte ne correspond aux filtres." />}
            />
          </Card>
        )}
      </AsyncBoundary>

      {choisi !== null && <Historique compte={choisi} onClose={() => setChoisi(null)} />}
    </>
  );
}

/** L'historique de connexions d'un compte, page par page. */
function Historique({ compte, onClose }: { compte: ConnectionSummary; onClose: () => void }) {
  const [curseur, setCurseur] = useState<string | undefined>(undefined);
  const [precedents, setPrecedents] = useState<ConnectionEvent[]>([]);
  const page = useApiQuery(
    (o) => api.listUserConnections(compte.user.id, curseur === undefined ? {} : { before_id: curseur }, o),
    [compte.user.id, curseur],
  );
  const evenements = [...precedents, ...(page.data?.events ?? [])];
  const suivant = page.data?.next_before_id ?? null;

  function chargerLaSuite() {
    if (suivant === null) {
      return;
    }
    setPrecedents(evenements);
    setCurseur(suivant);
  }

  const colonnes: TableColumn<ConnectionEvent>[] = [
    {
      key: "quand",
      header: "Quand",
      render: (e) => <time dateTime={isoDateTime(e.at)}>{formatDateTime(e.at)}</time>,
    },
    {
      key: "action",
      header: "Événement",
      render: (e) => (
        <Badge tone={ACTIONS[e.action].ton} size="sm" dot>
          {ACTIONS[e.action].libelle}
        </Badge>
      ),
    },
    {
      key: "motif",
      header: "Motif",
      render: (e) => (e.reason === null ? "" : (MOTIFS[e.reason] ?? e.reason)),
    },
  ];

  return (
    <Modal
      open
      onClose={onClose}
      title={`Connexions de ${compte.user.email}`}
      footer={
        <>
          {suivant !== null && (
            <Button variant="secondary" onClick={chargerLaSuite} loading={page.loading}>
              Charger la suite
            </Button>
          )}
          <Button variant="primary" onClick={onClose}>
            Fermer
          </Button>
        </>
      }
    >
      <p className={styles.resumeCompte}>
        {ROLE_LABELS[compte.user.role]}
        {compte.company_name !== null && ` · ${compte.company_name}`} · dernière connexion :{" "}
        <Quand iso={compte.last_login_at} vide="jamais" />
      </p>
      <Table
        columns={colonnes}
        rows={evenements}
        rowKey={(e) => e.id}
        caption="Du plus récent au plus ancien"
        loading={page.loading && evenements.length === 0}
        animateRows={false}
        empty={<EmptyState compact title="Aucune connexion enregistrée." description="Ce compte ne s'est jamais connecté." />}
      />
    </Modal>
  );
}
